import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Download, DollarSign, TrendingUp, TrendingDown, Receipt, Edit, Trash2, Calendar, ChevronDown, FileSpreadsheet, FileText, Table2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CURRENCY_SYMBOLS = {
  USD: "$", EUR: "€", GBP: "£", JPY: "¥", CAD: "CA$", AUD: "A$",
  CHF: "CHF", CNY: "¥", INR: "₹", MXN: "$", BRL: "R$", ZAR: "R",
  SGD: "S$", HKD: "HK$", KRW: "₩", SEK: "kr", NOK: "kr", DKK: "kr",
  NZD: "NZ$", THB: "฿"
};

const INCOME_CATEGORIES = ["Booking Income", "Cleaning Fee", "Extra Guest Fee", "Other Income"];
const EXPENSE_CATEGORIES = ["Cleaning", "Maintenance", "Utilities", "Supplies", "Other Expense"];

const formatCurrency = (amount) => `₹${amount.toLocaleString('en-IN')}`;

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [editFormData, setEditFormData] = useState({
    type: "income",
    category: "",
    amount: "",
    currency: "INR",
    date: "",
    description: "",
  });

  const getCurrencySymbol = (code) => CURRENCY_SYMBOLS[code] || code;

  const groupTransactionsByMonth = (transactions) => {
    const grouped = {};
    
    transactions.forEach(txn => {
      const monthKey = txn.date.substring(0, 7); // YYYY-MM
      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          monthKey,
          transactions: [],
          income: 0,
          expense: 0,
          net: 0
        };
      }
      
      grouped[monthKey].transactions.push(txn);
      
      if (txn.type === 'income') {
        grouped[monthKey].income += txn.amount;
      } else {
        grouped[monthKey].expense += txn.amount;
      }
      grouped[monthKey].net = grouped[monthKey].income - grouped[monthKey].expense;
    });
    
    // Sort by month (newest first)
    return Object.values(grouped).sort((a, b) => b.monthKey.localeCompare(a.monthKey));
  };

  const formatMonthName = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  };

  useEffect(() => {
    fetchPropertyData();
    fetchCurrencies();
  }, [id]);

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${API}/currencies`);
      setCurrencies(response.data.currencies);
    } catch (error) {
      console.error("Error fetching currencies:", error);
    }
  };

  const fetchPropertyData = async () => {
    try {
      const [propRes, summaryRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/properties/${id}`),
        axios.get(`${API}/properties/${id}/summary`),
        axios.get(`${API}/transactions?property_id=${id}`),
      ]);
      setProperty(propRes.data);
      setSummary(summaryRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error("Error fetching property data:", error);
      toast.error("Failed to load property data");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format = 'csv') => {
    const formatConfig = {
      csv: { endpoint: 'csv', extension: 'csv', name: 'CSV' },
      excel: { endpoint: 'excel', extension: 'xlsx', name: 'Excel' },
      pdf: { endpoint: 'pdf', extension: 'pdf', name: 'PDF' }
    };
    
    const config = formatConfig[format];
    if (!config) return;
    
    try {
      const response = await axios.get(`${API}/export/${config.endpoint}?property_id=${id}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `transactions_${property.name.replace(/\s+/g, '_')}.${config.extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Transactions exported as ${config.name} successfully!`);
    } catch (error) {
      console.error("Error exporting transactions:", error);
      toast.error("Failed to export transactions");
    }
  };

  const handleEditClick = (transaction) => {
    setSelectedTransaction(transaction);
    setEditFormData({
      type: transaction.type,
      category: transaction.category,
      amount: transaction.amount.toString(),
      currency: transaction.currency || "INR",
      date: transaction.date,
      description: transaction.description || "",
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTransaction) return;

    try {
      await axios.put(`${API}/transactions/${selectedTransaction.id}`, {
        ...editFormData,
        amount: parseFloat(editFormData.amount),
      });
      toast.success("Transaction updated successfully!");
      setEditDialogOpen(false);
      fetchPropertyData();
    } catch (error) {
      console.error("Error updating transaction:", error);
      toast.error("Failed to update transaction");
    }
  };

  const handleDeleteClick = (transaction) => {
    setSelectedTransaction(transaction);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTransaction) return;

    try {
      await axios.delete(`${API}/transactions/${selectedTransaction.id}`);
      toast.success("Transaction deleted successfully!");
      setDeleteDialogOpen(false);
      setSelectedTransaction(null);
      fetchPropertyData();
    } catch (error) {
      console.error("Error deleting transaction:", error);
      toast.error("Failed to delete transaction");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen" data-testid="property-detail-loading">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="flex items-center justify-center h-screen" data-testid="property-not-found">
        <div className="text-lg text-muted-foreground">Property not found</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-in" data-testid="property-detail">
      <Button
        variant="ghost"
        className="mb-6 hover:bg-muted transition-colors duration-200"
        onClick={() => navigate("/properties")}
        data-testid="back-button"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Properties
      </Button>

      {/* Property Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-primary" data-testid="property-name">
              {property.name}
            </h1>
            <p className="text-base text-muted-foreground mt-2">{property.address}</p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className="rounded-full font-medium transition-all duration-200 active:scale-95 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md"
                data-testid="export-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="export-csv-option">
                <Table2 className="w-4 h-4 mr-2 text-emerald-600" />
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('excel')} data-testid="export-excel-option">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                Export as Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf')} data-testid="export-pdf-option">
                <FileText className="w-4 h-4 mr-2 text-red-600" />
                Export as PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="bg-white rounded-xl border border-border/60 shadow-sm hover:shadow-md transition-all duration-300" data-testid="property-total-income">
          <CardHeader className="p-6 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-income" />
              Total Income
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            <div className="text-3xl font-bold text-income">{formatCurrency(summary?.total_income || 0)}</div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-xl border border-border/60 shadow-sm hover:shadow-md transition-all duration-300" data-testid="property-total-expense">
          <CardHeader className="p-6 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-expense" />
              Total Expenses
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            <div className="text-3xl font-bold text-expense">{formatCurrency(summary?.total_expense || 0)}</div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-xl border border-border/60 shadow-sm hover:shadow-md transition-all duration-300" data-testid="property-net-income">
          <CardHeader className="p-6 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Net Income
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            <div
              className={`text-3xl font-bold ${
                summary?.net_income >= 0 ? "text-income" : "text-expense"
              }`}
            >
              {formatCurrency(summary?.net_income || 0)}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-xl border border-border/60 shadow-sm hover:shadow-md transition-all duration-300" data-testid="property-transaction-count">
          <CardHeader className="p-6 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Receipt className="w-4 h-4" />
              Transactions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            <div className="text-3xl font-bold text-primary">{summary?.transaction_count || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Transactions List - Month-wise */}
      <Card className="bg-white rounded-xl border border-border/60 shadow-sm" data-testid="transactions-card">
        <CardHeader className="p-6 pb-4">
          <CardTitle className="text-2xl font-semibold text-primary flex items-center gap-2">
            <Calendar className="w-6 h-6" />
            Transactions by Month
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          {transactions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground" data-testid="no-transactions">
              No transactions yet. Add your first transaction to get started.
            </div>
          ) : (
            <Accordion type="single" collapsible defaultValue={groupTransactionsByMonth(transactions)[0]?.monthKey} className="w-full">
              {groupTransactionsByMonth(transactions).map((monthGroup) => (
                <AccordionItem key={monthGroup.monthKey} value={monthGroup.monthKey} className="border-b border-border">
                  <AccordionTrigger className="hover:no-underline py-4" data-testid={`month-accordion-${monthGroup.monthKey}`}>
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Calendar className="w-6 h-6 text-primary" />
                        </div>
                        <div className="text-left">
                          <h3 className="text-lg font-semibold text-primary">{formatMonthName(monthGroup.monthKey)}</h3>
                          <p className="text-sm text-muted-foreground">{monthGroup.transactions.length} transactions</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6 text-sm">
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Income</p>
                          <p className="font-semibold text-income">{formatCurrency(monthGroup.income)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Expense</p>
                          <p className="font-semibold text-expense">{formatCurrency(monthGroup.expense)}</p>
                        </div>
                        <div className="text-right min-w-[120px]">
                          <p className="text-xs text-muted-foreground">Net Income</p>
                          <p className={`font-bold text-lg ${monthGroup.net >= 0 ? 'text-income' : 'text-expense'}`}>
                            {formatCurrency(monthGroup.net)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="overflow-x-auto pt-2">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Date</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Category</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Type</th>
                            <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">Amount</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Description</th>
                            <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {monthGroup.transactions.map((txn) => (
                            <tr
                              key={txn.id}
                              className="border-b border-border/50 hover:bg-muted/30 transition-colors"
                              data-testid={`transaction-row-${txn.id}`}
                            >
                              <td className="py-3 px-4 text-sm">{txn.date}</td>
                              <td className="py-3 px-4 text-sm">{txn.category}</td>
                              <td className="py-3 px-4">
                                <Badge
                                  className={`rounded-md px-2.5 py-0.5 text-xs font-semibold ${
                                    txn.type === "income"
                                      ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                                      : "bg-rose-100 text-rose-700 border border-rose-200"
                                  }`}
                                >
                                  {txn.type}
                                </Badge>
                              </td>
                              <td className={`py-3 px-4 text-sm text-right font-semibold ${ 
                                txn.type === "income" ? "text-income" : "text-expense"
                              }`}>
                                {formatCurrency(txn.amount)}
                              </td>
                              <td className="py-3 px-4 text-sm text-muted-foreground">{txn.description || "-"}</td>
                              <td className="py-3 px-4">
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors duration-200"
                                    onClick={() => handleEditClick(txn)}
                                    data-testid={`edit-transaction-${txn.id}`}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors duration-200"
                                    onClick={() => handleDeleteClick(txn)}
                                    data-testid={`delete-transaction-${txn.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>

      {/* Edit Transaction Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[550px]" data-testid="edit-transaction-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">Edit Transaction</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Make changes to the transaction details below.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="edit-type">Transaction Type</Label>
              <Select value={editFormData.type} onValueChange={(value) => setEditFormData({ ...editFormData, type: value })}>
                <SelectTrigger className="mt-1" data-testid="edit-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit-category">Category</Label>
              <Select value={editFormData.category} onValueChange={(value) => setEditFormData({ ...editFormData, category: value })}>
                <SelectTrigger className="mt-1" data-testid="edit-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(editFormData.type === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-amount">Amount</Label>
                <Input
                  id="edit-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="edit-amount-input"
                  value={editFormData.amount}
                  onChange={(e) => setEditFormData({ ...editFormData, amount: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="edit-currency">Currency</Label>
                <Select value={editFormData.currency} onValueChange={(value) => setEditFormData({ ...editFormData, currency: value })}>
                  <SelectTrigger className="mt-1" data-testid="edit-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((currency) => (
                      <SelectItem key={currency.code} value={currency.code}>
                        {currency.symbol} {currency.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="edit-date">Date</Label>
              <Input
                id="edit-date"
                type="date"
                data-testid="edit-date-input"
                value={editFormData.date}
                onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="edit-description">Description (Optional)</Label>
              <Textarea
                id="edit-description"
                data-testid="edit-description-input"
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                className="mt-1"
                rows={3}
              />
            </div>

            <div className="flex gap-4">
              <Button
                type="submit"
                className="flex-1 rounded-full font-medium transition-all duration-200 active:scale-95 bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="update-transaction-button"
              >
                Update Transaction
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1 rounded-full font-medium transition-all duration-200 active:scale-95"
                onClick={() => setEditDialogOpen(false)}
                data-testid="cancel-edit-button"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-transaction-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-semibold">Delete Transaction?</AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              Are you sure you want to delete this transaction? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-txn-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-txn-button"
            >
              Delete Transaction
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}